import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { logActivity } from "../../utils/activity";
import Topbar from "../../components/Topbar/Topbar";
import ChecklistEditor from "./ChecklistEditor";
import ChecklistHistory from "./ChecklistHistory";
import Scoreboard from "./Scoreboard";
import ScoreSettings from "./ScoreSettings";
import HintEditor from "./HintEditor";
import { todayStr, startOfMonth, startOfYear } from "../../utils/dateRange";
import { loadGmHudConfig } from "../../utils/config";
import "./Admin.css";
import { formatTimestamp as formatTs } from "../../utils/format";

const GAMES_CLOUD_BASE = (process.env.REACT_APP_GAMES_CLOUD_URL || "")
    .replace(/^ws/, "http")
    .replace(/ws$/, "");

const PAGE_SIZE = 50;

const ACTION_LABELS = {
    login:              "Login",
    logout:             "Logout",
    open_room:          "Rum åbnet",
    close_room:         "Rum lukket",
    hint_sent:          "Hint sendt",
    timer_start:        "Timer startet",
    timer_pause:        "Timer pauset",
    timer_reset:        "Timer nulstillet",
    timer_modify:       "Timer ændret",
    overwrite_prop:     "Prop overskrevet",
    overwrite_flow:     "Flow overskrevet",
    open_admin:         "Admin",
    open_checklist:         "Todo-liste åbnet",
    checklist_task_done:    "Checklist: opgave udført",
    checklist_task_undone:  "Checklist: opgave fortrudt",
    checklist_task_create:  "Checklist: opgave oprettet",
    checklist_task_edit:    "Checklist: opgave rettet",
    checklist_task_delete:  "Checklist: opgave slettet",
    checklist_task_reorder: "Checklist: rækkefølge ændret",
    checklist_history_export: "Checklist: historik eksporteret",
    checklist_scoreboard_export: "Checklist: scoreboard eksporteret",
    scoreboard_export:          "Scoreboard eksporteret",
    scoreboard_settings_save:   "Scoreboard: satser gemt",
    scoreboard_team_create:     "Scoreboard: team oprettet",
    scoreboard_team_edit:       "Scoreboard: team rettet",
    scoreboard_team_delete:     "Scoreboard: team slettet",
    open_scoreboard:            "Scoreboard åbnet",
};

function actionClass(action) {
    return `action-${action}`.replace(/[^a-z0-9_-]/g, "_");
}

const EXPORT_PAGE = 200; // matches server maxPageSize

function csvCell(v) {
    const s = String(v ?? "");
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function eventsToCsv(events) {
    const header = ["Tidspunkt", "Bruger", "MedarbejderID", "Handling", "Detaljer"];
    const lines = events.map(ev => [
        formatTs(ev.timestamp),
        ev.userEmail,
        ev.employeeId || "",
        ACTION_LABELS[ev.action] || ev.action,
        ev.details || "",
    ].map(csvCell).join(","));
    // BOM so Excel reads UTF-8 (æøå) correctly; CRLF for Excel line endings.
    return "﻿" + [header.join(","), ...lines].join("\r\n");
}

function downloadCsv(filename, csv) {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

export default function Admin() {
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [unauthorized, setUnauthorized] = useState(false);
    const [config, setConfig] = useState(null);
    const [tab, setTab] = useState("logs"); // logs | lists | history | scores | points

    // Filters — applied server-side
    const [filterEmail, setFilterEmail]   = useState("");
    const [filterAction, setFilterAction] = useState("");
    const [filterFrom, setFilterFrom]     = useState("");
    const [filterTo, setFilterTo]         = useState("");

    // Distinct action types seen so far (for dropdown)
    const [knownActions, setKnownActions] = useState(Object.keys(ACTION_LABELS));

    const fetchPage = useCallback(async (pageOffset) => {
        const token = localStorage.getItem("accessToken");
        if (!token) { navigate("/"); return; }
        setLoading(true);
        try {
            const params = new URLSearchParams({ limit: PAGE_SIZE, offset: pageOffset });
            if (filterEmail)  params.set("email",  filterEmail);
            if (filterAction) params.set("action", filterAction);
            if (filterFrom)   params.set("from", new Date(filterFrom).getTime());
            if (filterTo)     params.set("to",   new Date(filterTo + "T23:59:59").getTime());

            const r = await fetch(`${GAMES_CLOUD_BASE}activity?${params}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!r.ok) { setUnauthorized(true); return; }
            const data = await r.json();
            setEvents(data.events || []);
            setTotal(data.total || 0);
            setOffset(pageOffset);
        } catch {
            setEvents([]);
        } finally {
            setLoading(false);
        }
    }, [navigate, filterEmail, filterAction, filterFrom, filterTo]);

    // Load config once for the admin gate + the checklist tabs' location list.
    useEffect(() => {
        const email = localStorage.getItem("userEmail") || "";
        loadGmHudConfig().then(cfg => {
            if (!cfg?.users?.[email]?.admin) { setUnauthorized(true); return; }
            setConfig(cfg);
        });
    }, []);

    // Fetch the activity log only while its tab is active; reset to page 0 on filter change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { if (tab === "logs") fetchPage(0); }, [tab, filterEmail, filterAction, filterFrom, filterTo]);

    function handleLogout() {
        logActivity("logout");
        localStorage.removeItem("accessToken");
        localStorage.removeItem("userEmail");
        navigate("/");
    }

    // Fetch every matching event by paging through the API (respects the active
    // filters when useFilters is true; otherwise pulls the whole log).
    const fetchAllEvents = useCallback(async (useFilters) => {
        const token = localStorage.getItem("accessToken");
        if (!token) { navigate("/"); return []; }

        const base = new URLSearchParams();
        if (useFilters) {
            if (filterEmail)  base.set("email",  filterEmail);
            if (filterAction) base.set("action", filterAction);
            if (filterFrom)   base.set("from", new Date(filterFrom).getTime());
            if (filterTo)     base.set("to",   new Date(filterTo + "T23:59:59").getTime());
        }

        const all = [];
        let pageOffset = 0;
        for (;;) {
            const p = new URLSearchParams(base);
            p.set("limit", EXPORT_PAGE);
            p.set("offset", pageOffset);
            const r = await fetch(`${GAMES_CLOUD_BASE}activity?${p}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!r.ok) throw new Error("fetch failed");
            const data = await r.json();
            const evs = data.events || [];
            all.push(...evs);
            pageOffset += evs.length;
            if (evs.length === 0 || pageOffset >= (data.total || 0)) break;
        }
        return all;
    }, [navigate, filterEmail, filterAction, filterFrom, filterTo]);

    const handleExport = useCallback(async (useFilters) => {
        setExporting(true);
        try {
            const events = await fetchAllEvents(useFilters);
            if (!events.length) { alert("Ingen events at eksportere."); return; }
            const today = new Date().toISOString().slice(0, 10);
            const filename = useFilters
                ? `aktivitetslog_${filterFrom || "start"}_${filterTo || today}.csv`
                : `aktivitetslog_alle_${today}.csv`;
            downloadCsv(filename, eventsToCsv(events));
            logActivity("export_activity_csv", useFilters ? `${filterFrom || "-"}..${filterTo || "-"}` : "alle");
        } catch {
            alert("Eksport fejlede — prøv igen.");
        } finally {
            setExporting(false);
        }
    }, [fetchAllEvents, filterFrom, filterTo]);

    if (unauthorized) return (
        <div style={{ padding: 40, color: "#e74c3c" }}>
            Ingen adgang. <button onClick={() => navigate("/")} style={{ marginLeft: 12, cursor: "pointer" }}>Tilbage</button>
        </div>
    );

    const totalPages = Math.ceil(total / PAGE_SIZE);
    const currentPage = Math.floor(offset / PAGE_SIZE);

    // Which date-range chip (if any) matches the current log filters — for highlighting.
    const activeLogPreset =
        (!filterFrom && !filterTo) ? "all"
            : (filterFrom === todayStr() && filterTo === todayStr()) ? "today"
                : (filterFrom === startOfMonth() && filterTo === todayStr()) ? "month"
                    : (filterFrom === startOfYear() && filterTo === todayStr()) ? "year" : "";

    return (
        <>
            <Topbar
                onLogout={handleLogout}
            />

            <div className="admin-content">
                <div className="admin-tabs">
                    <button className={`admin-tab ${tab === "logs" ? "active" : ""}`} onClick={() => setTab("logs")}>Logs</button>
                    <button className={`admin-tab ${tab === "lists" ? "active" : ""}`} onClick={() => setTab("lists")}>Todo-lister</button>
                    <button className={`admin-tab ${tab === "history" ? "active" : ""}`} onClick={() => setTab("history")}>Historik</button>
                    <button className={`admin-tab ${tab === "scores" ? "active" : ""}`} onClick={() => setTab("scores")}>Scoreboard</button>
                    <button className={`admin-tab ${tab === "points" ? "active" : ""}`} onClick={() => setTab("points")}>Point</button>
                    <button className={`admin-tab ${tab === "hints" ? "active" : ""}`} onClick={() => setTab("hints")}>Hints</button>
                </div>

                {tab === "lists" && <ChecklistEditor config={config} />}
                {tab === "history" && <ChecklistHistory config={config} />}
                {tab === "scores" && <Scoreboard config={config} />}
                {tab === "points" && <ScoreSettings config={config} />}
                {tab === "hints" && <HintEditor />}

                {tab === "logs" && (<>
                <div className="acl-scope">
                    <label>Email
                        <input type="text" placeholder="Filtrer på email…" value={filterEmail}
                            onChange={e => setFilterEmail(e.target.value)} />
                    </label>
                    <label>Handling
                        <select value={filterAction} onChange={e => setFilterAction(e.target.value)}>
                            <option value="">Alle handlinger</option>
                            {knownActions.map(a => (
                                <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>
                            ))}
                        </select>
                    </label>
                    <label>Fra<input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} /></label>
                    <label>Til<input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)} /></label>
                    <div className="acl-presets">
                        <button className={`acl-preset ${activeLogPreset === "today" ? "active" : ""}`}
                            onClick={() => { setFilterFrom(todayStr()); setFilterTo(todayStr()); }}>I dag</button>
                        <button className={`acl-preset ${activeLogPreset === "month" ? "active" : ""}`}
                            onClick={() => { setFilterFrom(startOfMonth()); setFilterTo(todayStr()); }}>Denne måned</button>
                        <button className={`acl-preset ${activeLogPreset === "year" ? "active" : ""}`}
                            onClick={() => { setFilterFrom(startOfYear()); setFilterTo(todayStr()); }}>I år</button>
                        <button className={`acl-preset ${activeLogPreset === "all" ? "active" : ""}`}
                            onClick={() => { setFilterFrom(""); setFilterTo(""); }}>Alt</button>
                    </div>
                </div>

                <div className="acl-card">
                    <div className="acl-card-head">
                        <h2>Aktivitetslog</h2>
                        <div className="admin-actions">
                            <button className="admin-refresh-btn" onClick={() => fetchPage(offset)} disabled={loading}>
                                {loading ? "Henter…" : "Opdater"}
                            </button>
                            <button className="admin-refresh-btn admin-export-btn" onClick={() => handleExport(true)} disabled={exporting || loading}
                                title="Download loggen for de valgte filtre/periode som CSV">
                                {exporting ? "Eksporterer…" : "Download valgte (CSV)"}
                            </button>
                            <button className="admin-refresh-btn admin-export-btn" onClick={() => handleExport(false)} disabled={exporting || loading}
                                title="Download hele aktivitetsloggen som CSV">
                                Download alle (CSV)
                            </button>
                        </div>
                    </div>

                {events.length === 0 ? (
                    <div className="admin-empty">{loading ? "Henter…" : "Ingen events fundet."}</div>
                ) : (
                    <>
                        <div className="activity-table-wrap">
                            <table className="activity-table">
                                <thead>
                                    <tr>
                                        <th>Tidspunkt</th>
                                        <th>Bruger</th>
                                        <th>Handling</th>
                                        <th>Detaljer</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {events.map((ev, i) => (
                                        <tr key={i}>
                                            <td style={{ whiteSpace: "nowrap" }}>{formatTs(ev.timestamp)}</td>
                                            <td>{ev.userEmail}</td>
                                            <td>
                                                <span className={`action-badge ${actionClass(ev.action)}`}>
                                                    {ACTION_LABELS[ev.action] || ev.action}
                                                </span>
                                            </td>
                                            <td style={{ color: "var(--text-dim)" }}>{ev.details || "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="pagination">
                            <span className="pagination-info">
                                {total} events · side {currentPage + 1} af {totalPages || 1}
                            </span>
                            <div className="pagination-controls">
                                <button
                                    className="page-btn"
                                    disabled={offset === 0 || loading}
                                    onClick={() => fetchPage(0)}
                                >«</button>
                                <button
                                    className="page-btn"
                                    disabled={offset === 0 || loading}
                                    onClick={() => fetchPage(Math.max(0, offset - PAGE_SIZE))}
                                >‹</button>
                                <button
                                    className="page-btn"
                                    disabled={offset + PAGE_SIZE >= total || loading}
                                    onClick={() => fetchPage(offset + PAGE_SIZE)}
                                >›</button>
                                <button
                                    className="page-btn"
                                    disabled={offset + PAGE_SIZE >= total || loading}
                                    onClick={() => fetchPage((totalPages - 1) * PAGE_SIZE)}
                                >»</button>
                            </div>
                        </div>
                    </>
                )}
                </div>
                </>)}
            </div>
        </>
    );
}
